/**
 * Reader for the openScope airport JSON (MIT), extended with an optional `x_atc` block of
 * our own (see schema/airport.schema.json and docs/airport-format.md).
 *
 * Everything the game needs is parsed here once: reference point, runways, fixes, SIDs and
 * STARs (with altitude/speed restrictions), airspace, restricted areas, video maps, spawn
 * patterns, radio names, wind. Consumers never touch the raw JSON.
 */
import { parseOpenScopeCoordinate, type OpenScopePosition } from './geo/openscope-coords';
import { bearingDeg, distanceM } from './geo/projection';
import type { AircraftCategory } from './types';

export interface RunwayEnd {
  name: string;
  lat: number;
  lon: number;
  elevationM: number;
  ils: boolean;
  glideslopeDeg: number;
  /** True heading when landing on this end (from this threshold toward the far end). */
  headingDeg: number;
}

export interface Runway {
  /** Pair name e.g. "10L/28R". */
  name: string;
  ends: [RunwayEnd, RunwayEnd];
  lengthM: number;
}

export interface Fix {
  name: string;
  lat: number;
  lon: number;
  /** openScope convention: names starting with "_" are internal (DME/computed) and not drawn. */
  hidden: boolean;
}

/** Altitude/speed restriction on a route leg, from openScope strings like "A100+|A120-|S250". */
export interface Restriction {
  altitudeFt?: number;
  altitudeMinFt?: number;
  altitudeMaxFt?: number;
  speedKt?: number;
  speedMinKt?: number;
  speedMaxKt?: number;
}

export type RouteLeg =
  | { kind: 'fix'; fix: string; restriction?: Restriction }
  /** openScope "#140": fly heading 140 after the previous fix (vectors expected). */
  | { kind: 'heading'; headingDeg: number };

export type ApproachType = 'ils' | 'loc' | 'rnp' | 'visual';
export type ApproachFixRole = 'IAF' | 'IF' | 'FAF' | 'MAPt';

export interface ApproachLeg { fix: string; role?: ApproachFixRole; restriction?: Restriction }

/** An instrument approach to one runway end: published (x_atc.approaches) or synthesized (core/src/approach.ts). */
export interface InstrumentApproach {
  /** e.g. "ILS12". */
  id: string;
  /** Chart name, e.g. "ILS RWY 12". */
  name: string;
  type: ApproachType;
  runway: string;
  /** From the first approach fix to the final approach fix or point, in order. */
  legs: ApproachLeg[];
  missed?: RouteLeg[];
  navaid?: { ident: string; frequencyMHz: number };
  source: 'published' | 'synthesized';
}

export interface Procedure {
  icao: string;
  name: string;
  kind: 'sid' | 'star';
  /** SID top altitude in feet, when given. */
  altitudeFt?: number;
  /** Runway-specific legs keyed by "<ICAO><runway>", e.g. "KSFO28L". */
  rwy: Record<string, RouteLeg[]>;
  body: RouteLeg[];
  /** SID: legs after the body keyed by exit fix. */
  exitPoints: Record<string, RouteLeg[]>;
  /** STAR: legs before the body keyed by entry fix. */
  entryPoints: Record<string, RouteLeg[]>;
  /** Polylines of fix names for drawing; a trailing "*" marks a label position in openScope. */
  draw: string[][];
}

export interface SpawnAirline {
  /** ICAO airline code, lower case in openScope files; normalised to upper case here. */
  code: string;
  /** Fleet name after the slash, e.g. "long" in "ual/long". */
  fleet?: string;
  weight: number;
}

export interface SpawnPattern {
  origin: string;
  destination: string;
  category: 'arrival' | 'departure' | 'overflight';
  /** openScope route string, e.g. "AMAKR.BDEGA3.KSFO28L" or "KSFO28L.GNNRR2.AMAKR". */
  route: string;
  altitudeFt?: number | [number, number];
  speedKt?: number;
  method: string;
  /** Aircraft per hour. */
  rateHourly: number;
  airlines: SpawnAirline[];
}

export interface LatLon { lat: number; lon: number }

export interface AirspaceVolume {
  floorFt: number;
  ceilingFt: number;
  airspaceClass: string;
  poly: LatLon[];
}

export interface RestrictedArea {
  name: string;
  heightFt: number;
  poly: LatLon[];
}

export interface VideoMap {
  name: string;
  /** Line segments in degrees. Stored flat for speed: [lat1, lon1, lat2, lon2, ...]. */
  segments: Float64Array;
}

/** Our extension block; every field optional, see docs/airport-format.md. */
export interface AtcExtension {
  /** Explicit game approximation for independent parallel approach ends. No implicit exemptions. */
  independentApproaches?: [string, string][];
  tower?: { lat: number; lon: number; eyeHeightAglM?: number };
  imagery?: 'usgs' | 'eox' | 'osm';
  /** IANA time zone, e.g. "Asia/Jerusalem". */
  timezone?: string;
  /** Airline code → spoken name, overriding the built-in table. */
  telephony?: Record<string, string>;
  /** Accent the controller speaks with on synthesized radio (voice/speech.mjs ACCENTS key). Defaults from the ICAO prefix. */
  accent?: string;
  /** Airline code → accent key, overriding the built-in table in sim/src/airlines.ts. */
  accents?: Record<string, string>;
  /** How flight numbers are spoken: "icao" digit by digit, "faa" in groups. Defaults from the ICAO prefix (K, P → faa). */
  radioStyle?: 'icao' | 'faa';
  /** Extra pilot wording used only at this airport, per phrase kind (sim/src/phraseology.json); mid and high realism only. */
  phraseExtras?: Record<string, [string, number][]>;
  /** Fleet tables: airline code → weighted ICAO types. */
  fleets?: Record<string, [string, number][]>;
  sources?: { item: string; url?: string; date?: string; note?: string }[];
  notes?: string[];
  /** Published instrument approaches; restrictions use openScope strings ("A30", "A40+"). */
  approaches?: { id?: string; name: string; type: ApproachType; runway: string; legs: { fix: string; role?: ApproachFixRole; restriction?: string }[]; missed?: RawLeg[]; navaid?: { ident: string; frequencyMHz: number } }[];
  /** Published holding patterns: "hold at FIX as published" flies these. */
  holds?: PublishedHold[];
  /** Preferential runway system: runways in order of preference with their tailwind limits (knots). */
  runwayUse?: { arrival?: { runway: string; maxTailwindKt: number }[]; departure?: { runway: string; maxTailwindKt: number }[] };
  /** Diversion airports, ICAO codes, nearest first. */
  alternates?: string[];
  /** Authority index used by the chart importer. */
  chartSource?: ChartSource;
  /** Generated, checked-in snapshot and chart catalog. */
  charts?: AirportChartLibrary;
}

/** A published holding pattern, as charted: the inbound course is MAGNETIC (`toTrueDeg` for flying it). */
export interface PublishedHold {
  fix: string;
  inboundDeg: number;
  turn: 'L' | 'R';
  /** Outbound leg, seconds. */
  legS: number;
  minAltitudeFt?: number;
  maxIasKt?: number;
}

export type ChartCategory = 'aerodrome' | 'sid' | 'star' | 'approach';
export type ChartProvider = 'faa-dtpp' | 'eaip-html' | 'explicit';

export interface ChartSourceDocument { label?: string; url: string }
export interface ChartSourceEntry {
  id?: string;
  title: string;
  category: ChartCategory;
  procedureNames?: string[];
  runways?: string[];
  documents: ChartSourceDocument[];
}

export interface ChartSource {
  provider: ChartProvider;
  /** Authority page, XML metafile, or reviewed provenance URL. */
  indexUrl: string;
  /** Must equal the airport's top-level AIRAC. */
  airac: number;
  effectiveFrom: string;
  effectiveTo?: string;
  /** Optional PDF root for indexes whose links are relative or, for FAA, live under /pdf/. */
  pdfBaseUrl?: string;
  /** Reviewed records used only by the explicit provider. */
  entries?: ChartSourceEntry[];
  /** Authority links intentionally outside the operational chart set. */
  exclusions?: { url: string; reason: string }[];
}

export interface AirportChartDocument {
  label: string;
  relativePath: string;
  sourceUrl: string;
  sha256: string;
  pages: number;
}

export interface AirportChart {
  id: string;
  title: string;
  category: ChartCategory;
  procedureNames: string[];
  /** Empty means the chart applies to all or is not runway-specific. */
  runways: string[];
  documents: AirportChartDocument[];
  airac: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface ChartListingEntry {
  title: string;
  sourceUrl: string;
  category?: ChartCategory;
  chartId?: string;
  excludedReason?: string;
}

export interface AirportChartLibrary {
  airac: number;
  effectiveFrom: string;
  effectiveTo?: string;
  sourceIndex: { url: string; sha256: string };
  /** Offline completeness snapshot of the authority listing used by the import. */
  listing: ChartListingEntry[];
  items: AirportChart[];
}

export interface AirportData {
  icao: string;
  iata: string;
  /** Tower radio name, e.g. "San Francisco Tower". */
  name: string;
  radio: { twr: string; app: string; dep: string };
  airac?: number;
  lat: number;
  lon: number;
  elevationM: number;
  elevationFt: number;
  /** Magnetic variation in degrees, positive east (true = magnetic + variation). */
  magneticNorthDeg: number;
  /** Control radius (nm) and ceiling (ft) of the simulated airspace. */
  ctrRadiusNm: number;
  ctrCeilingFt: number;
  /** Altitude departures are initially cleared to, feet. */
  initialAltitudeFt: number;
  wind: { directionDeg: number; speedKt: number };
  rangeRings: { enabled: boolean; center: LatLon; radiusNm: number };
  runways: Runway[];
  arrivalRunway: string;
  departureRunway: string;
  fixes: Map<string, Fix>;
  sids: Map<string, Procedure>;
  stars: Map<string, Procedure>;
  airspace: AirspaceVolume[];
  restricted: RestrictedArea[];
  maps: VideoMap[];
  defaultMaps: string[];
  spawnPatterns: SpawnPattern[];
  /** Published approaches only; use defaultApproach() for a runway without one. */
  approaches: InstrumentApproach[];
  /** Official authority-issued chart PDFs available for this airport. */
  charts: AirportChart[];
  chartLibrary?: AirportChartLibrary;
  xAtc: AtcExtension;
  /** Optional OSM-derived taxi graph. Browser loading attaches it; headless callers may omit it. */
  groundNetwork?: GroundNetwork;
}

export interface GroundNode { id: number; lat: number; lon: number; edges: { to: number; distanceM: number }[] }
export interface GroundNetwork { nodes: GroundNode[]; stands: number[] }

/** Build a compact routable graph from OSM taxiway lines and parking positions. */
export function parseOsmGroundNetwork(airport: AirportData, raw: any): GroundNetwork {
  const nodes: GroundNode[] = [], byKey = new Map<string, number>();
  const key = (lat: number, lon: number) => `${lat.toFixed(5)},${lon.toFixed(5)}`;
  const node = (lat: number, lon: number) => {
    const k = key(lat, lon), prior = byKey.get(k);
    if (prior !== undefined) return prior;
    const id = nodes.length; nodes.push({ id, lat, lon, edges: [] }); byKey.set(k, id); return id;
  };
  const connect = (a: number, b: number) => {
    if (a === b) return;
    const x = nodes[a]!, y = nodes[b]!, d = distanceM(x.lat, x.lon, y.lat, y.lon);
    if (!x.edges.some((e) => e.to === b)) x.edges.push({ to: b, distanceM: d });
    if (!y.edges.some((e) => e.to === a)) y.edges.push({ to: a, distanceM: d });
  };
  const parking: { lat: number; lon: number }[] = [];
  for (const f of raw?.features ?? []) {
    const p = f?.properties ?? {}, g = f?.geometry;
    if (g?.type === 'LineString' && p.aeroway === 'taxiway') {
      let prev: number | undefined;
      for (const c of g.coordinates ?? []) {
        if (!Array.isArray(c) || c.length < 2) continue;
        const id = node(Number(c[1]), Number(c[0])); if (prev !== undefined) connect(prev, id); prev = id;
      }
    } else if (g?.type === 'Point' && (p.aeroway === 'parking_position' || p.aeroway === 'gate')) {
      parking.push({ lat: Number(g.coordinates[1]), lon: Number(g.coordinates[0]) });
    }
  }
  const nearest = (p: { lat: number; lon: number }, maxM: number) => {
    let best = -1, bd = maxM;
    for (const n of nodes) { const d = distanceM(p.lat, p.lon, n.lat, n.lon); if (d < bd) { bd = d; best = n.id; } }
    return best;
  };
  let stands = [...new Set(parking.map((p) => nearest(p, 350)).filter((n) => n >= 0))];
  // Some extracts omit parking nodes. Start on connected taxiway nodes near the airport reference
  // point; routing still remains on real OSM pavement.
  if (!stands.length) stands = nodes.filter((n) => n.edges.length && distanceM(n.lat, n.lon, airport.lat, airport.lon) < 2500).slice(0, 12).map((n) => n.id);
  return { nodes, stands };
}

interface RawRunway {
  name: [string, string];
  end: [OpenScopePosition, OpenScopePosition];
  ils?: [boolean, boolean];
  glideslope?: [number, number];
}

type RawLeg = string | [string, string];

const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
};

/** "A100+|A120-|S250" → structured restriction. Altitudes are hundreds of feet. */
export function parseRestriction(s: string | undefined): Restriction | undefined {
  if (!s) return undefined;
  const r: Restriction = {};
  for (const part of s.split('|')) {
    const m = /^([AS])(\d+)([+-]?)$/.exec(part.trim());
    if (!m) continue;
    const v = Number(m[2]);
    if (m[1] === 'A') {
      const ft = v * 100;
      if (m[3] === '+') r.altitudeMinFt = ft; else if (m[3] === '-') r.altitudeMaxFt = ft; else r.altitudeFt = ft;
    } else {
      if (m[3] === '+') r.speedMinKt = v; else if (m[3] === '-') r.speedMaxKt = v; else r.speedKt = v;
    }
  }
  return Object.keys(r).length ? r : undefined;
}

function parseLeg(raw: RawLeg): RouteLeg {
  const [name, restriction] = typeof raw === 'string' ? [raw, undefined] : raw;
  if (name.startsWith('#')) return { kind: 'heading', headingDeg: Number(name.slice(1)) };
  // openScope also uses "^FIX" (fly-over) and "@FIX" (hold) prefixes in some files; keep the name.
  const fix = name.replace(/^[\^@]/, '');
  const r = parseRestriction(restriction);
  return r ? { kind: 'fix', fix, restriction: r } : { kind: 'fix', fix };
}

function parseLegs(raw: RawLeg[] | undefined, variationDeg = 0): RouteLeg[] {
  // Heading legs are charted magnetic; the sim flies true.
  return (raw ?? []).map(parseLeg).map((l) => (l.kind === 'heading' ? { kind: 'heading', headingDeg: normDeg(l.headingDeg + variationDeg) } : l));
}

const normDeg = (d: number) => ((d % 360) + 360) % 360;
/**
 * Headings people say, type, read and chart are magnetic; the flight model and the renderers work
 * in true (`airport.magneticNorthDeg` is the variation, east positive: true = magnetic + variation).
 * These two are the only conversions: at the edges, never inside the geometry.
 */
export const toTrueDeg = (airport: Pick<AirportData, 'magneticNorthDeg'>, magneticDeg: number): number => normDeg(magneticDeg + airport.magneticNorthDeg);
export const toMagneticDeg = (airport: Pick<AirportData, 'magneticNorthDeg'>, trueDeg: number): number => normDeg(trueDeg - airport.magneticNorthDeg);

function parseLegMap(raw: Record<string, RawLeg[]> | undefined, variationDeg: number): Record<string, RouteLeg[]> {
  const out: Record<string, RouteLeg[]> = {};
  for (const [k, v] of Object.entries(raw ?? {})) out[k] = parseLegs(v, variationDeg);
  return out;
}

function parseProcedure(raw: any, kind: 'sid' | 'star', variationDeg: number): Procedure {
  return {
    icao: String(raw.icao),
    name: String(raw.name ?? raw.icao),
    kind,
    altitudeFt: raw.altitude !== undefined && raw.altitude !== '' ? num(raw.altitude, 0) : undefined,
    rwy: parseLegMap(raw.rwy, variationDeg),
    body: parseLegs(raw.body, variationDeg),
    exitPoints: parseLegMap(raw.exitPoints, variationDeg),
    entryPoints: parseLegMap(raw.entryPoints, variationDeg),
    draw: (raw.draw ?? []) as string[][],
  };
}

function parsePoly(raw: OpenScopePosition[] | undefined): LatLon[] {
  return (raw ?? []).map((p) => { const [lat, lon] = parseOpenScopeCoordinate(p); return { lat, lon }; });
}

function parseHeightFt(v: unknown): number {
  if (typeof v === 'number') return v;
  const s = String(v ?? '0').trim().toLowerCase();
  if (s.endsWith('ft')) return Number(s.slice(0, -2));
  if (s.endsWith('m')) return Number(s.slice(0, -1)) / 0.3048;
  return Number(s) || 0;
}

export function parseOpenScopeAirport(raw: any): AirportData {
  const [lat, lon, elevationM] = parseOpenScopeCoordinate(raw.position);
  const runways: Runway[] = (raw.runways as RawRunway[]).map((r) => {
    const [a, b] = r.end.map(parseOpenScopeCoordinate) as [[number, number, number], [number, number, number]];
    const headingAtoB = bearingDeg(a[0], a[1], b[0], b[1]);
    const headingBtoA = bearingDeg(b[0], b[1], a[0], a[1]);
    const mk = (name: string, p: [number, number, number], ils: boolean | undefined, gs: number | undefined, heading: number): RunwayEnd => ({
      name, lat: p[0], lon: p[1], elevationM: p[2], ils: ils ?? false, glideslopeDeg: gs ?? 3, headingDeg: heading,
    });
    return {
      name: `${r.name[0]}/${r.name[1]}`,
      ends: [mk(r.name[0], a, r.ils?.[0], r.glideslope?.[0], headingAtoB), mk(r.name[1], b, r.ils?.[1], r.glideslope?.[1], headingBtoA)],
      lengthM: distanceM(a[0], a[1], b[0], b[1]),
    };
  });

  const fixes = new Map<string, Fix>();
  for (const [name, pos] of Object.entries(raw.fixes ?? {})) {
    const [flat, flon] = parseOpenScopeCoordinate(pos as OpenScopePosition);
    fixes.set(name, { name, lat: flat, lon: flon, hidden: name.startsWith('_') });
  }
  const sids = new Map<string, Procedure>();
  const variationDeg = num(raw.magnetic_north, 0);
  for (const [k, v] of Object.entries(raw.sids ?? {})) sids.set(k, parseProcedure(v, 'sid', variationDeg));
  const stars = new Map<string, Procedure>();
  for (const [k, v] of Object.entries(raw.stars ?? {})) stars.set(k, parseProcedure(v, 'star', variationDeg));

  const airspace: AirspaceVolume[] = (raw.airspace ?? []).map((a: any) => ({
    floorFt: num(a.floor, 0) * 100,
    ceilingFt: num(a.ceiling, 0) * 100,
    airspaceClass: String(a.airspace_class ?? ''),
    poly: parsePoly(a.poly),
  }));
  const restricted: RestrictedArea[] = (raw.restricted ?? []).map((a: any) => ({
    name: String(a.name ?? ''), heightFt: parseHeightFt(a.height), poly: parsePoly(a.poly),
  }));
  const maps: VideoMap[] = (raw.maps ?? []).map((m: any) => {
    const lines = (m.lines ?? []) as [OpenScopePosition[0], OpenScopePosition[0], OpenScopePosition[0], OpenScopePosition[0]][];
    const segments = new Float64Array(lines.length * 4);
    lines.forEach((l, i) => {
      const [a, b] = parseOpenScopeCoordinate([l[0], l[1]]);
      const [c, d] = parseOpenScopeCoordinate([l[2], l[3]]);
      segments[i * 4] = a; segments[i * 4 + 1] = b; segments[i * 4 + 2] = c; segments[i * 4 + 3] = d;
    });
    return { name: String(m.name ?? 'map'), segments };
  });

  const spawnPatterns: SpawnPattern[] = (raw.spawnPatterns ?? []).map((p: any) => {
    let altitudeFt: number | [number, number] | undefined;
    if (Array.isArray(p.altitude)) altitudeFt = [num(p.altitude[0], 0), num(p.altitude[1], 0)];
    else if (p.altitude !== '' && p.altitude !== undefined) altitudeFt = num(p.altitude, 0);
    return {
      origin: String(p.origin ?? ''),
      destination: String(p.destination ?? ''),
      category: p.category,
      route: String(p.route ?? ''),
      altitudeFt,
      speedKt: p.speed !== '' && p.speed !== undefined ? num(p.speed, 0) : undefined,
      method: String(p.method ?? 'random'),
      rateHourly: num(p.rate, 0),
      airlines: ((p.airlines ?? []) as [string, number][]).map(([spec, w]) => {
        const [code, fleet] = spec.split('/');
        return { code: code!.toUpperCase(), fleet, weight: num(w, 1) };
      }),
    };
  });

  const rr = raw.rangeRings ?? {};
  const rrCenter = rr.center ? parseOpenScopeCoordinate(rr.center) : [lat, lon];
  const x = (raw.x_atc ?? {}) as AtcExtension;

  return {
    icao: String(raw.icao).toUpperCase(),
    iata: String(raw.iata ?? ''),
    name: raw.radio?.twr ?? raw.icao,
    radio: { twr: String(raw.radio?.twr ?? `${raw.icao} Tower`), app: String(raw.radio?.app ?? `${raw.icao} Approach`), dep: String(raw.radio?.dep ?? `${raw.icao} Departure`) },
    airac: raw.airac !== undefined ? num(raw.airac, 0) : undefined,
    lat,
    lon,
    elevationM,
    elevationFt: elevationM / 0.3048,
    magneticNorthDeg: variationDeg,
    ctrRadiusNm: num(raw.ctr_radius, 80),
    ctrCeilingFt: num(raw.ctr_ceiling, 10000),
    initialAltitudeFt: num(raw.initial_alt, 5000),
    wind: { directionDeg: num(raw.wind?.angle, 0), speedKt: num(raw.wind?.speed, 0) },
    rangeRings: { enabled: rr.enabled !== false, center: { lat: rrCenter[0]!, lon: rrCenter[1]! }, radiusNm: num(rr.radius_nm, 5) },
    runways,
    arrivalRunway: String(raw.arrivalRunway ?? runways[0]?.ends[0].name ?? ''),
    departureRunway: String(raw.departureRunway ?? runways[0]?.ends[0].name ?? ''),
    fixes,
    sids,
    stars,
    airspace,
    restricted,
    maps,
    defaultMaps: (raw.defaultMaps ?? maps.map((m) => m.name)) as string[],
    spawnPatterns,
    approaches: (x.approaches ?? []).map((a): InstrumentApproach => ({
      id: a.id ?? `${a.type.toUpperCase()}${a.runway}`,
      name: a.name,
      type: a.type,
      runway: a.runway,
      legs: a.legs.map((l) => { const r = parseRestriction(l.restriction); return { fix: l.fix, ...(l.role ? { role: l.role } : {}), ...(r ? { restriction: r } : {}) }; }),
      ...(a.missed ? { missed: parseLegs(a.missed, variationDeg) } : {}),
      ...(a.navaid ? { navaid: a.navaid } : {}),
      source: 'published',
    })),
    charts: x.charts?.items ?? [],
    chartLibrary: x.charts,
    xAtc: x,
  };
}

export function findRunwayEnd(airport: AirportData, name: string): RunwayEnd | undefined {
  for (const r of airport.runways) for (const e of r.ends) if (e.name === name) return e;
  return undefined;
}

export function oppositeEnd(airport: AirportData, name: string): RunwayEnd | undefined {
  for (const r of airport.runways) {
    if (r.ends[0].name === name) return r.ends[1];
    if (r.ends[1].name === name) return r.ends[0];
  }
  return undefined;
}

/** Runway ends the fix-style lookups accept: "28R", "RW28R", "KSFO28R". */
export function runwayEndFromKey(airport: AirportData, key: string): RunwayEnd | undefined {
  const k = key.toUpperCase();
  const stripped = k.startsWith(airport.icao) ? k.slice(airport.icao.length) : k.startsWith('RW') ? k.slice(2) : k;
  return findRunwayEnd(airport, stripped);
}

const HEAVY_PREFIXES = ['A33', 'A34', 'A35', 'A38', 'A3ST', 'B74', 'B75', 'B76', 'B77', 'B78', 'MD11', 'DC10', 'A124', 'A225', 'IL96'];
const HEAVY_EXACT = new Set(['C17', 'C5M', 'K35R', 'A400']);
const LIGHT_PREFIXES = ['C1', 'C2', 'C3', 'C4', 'C5', 'PA', 'SR2', 'DA4', 'DA2', 'BE3', 'BE5', 'BE9', 'P28', 'M20', 'DV20', 'AC', 'GA', 'RV', 'C82', 'C20'];

/** Wake/model category from an ICAO type designator. The sim or airport can override. */
export function categoryForType(type: string): AircraftCategory {
  const t = type.toUpperCase();
  if (HEAVY_EXACT.has(t) || HEAVY_PREFIXES.some((p) => t.startsWith(p))) return 'heavy';
  if (LIGHT_PREFIXES.some((p) => t.startsWith(p))) return 'light';
  return 'medium';
}
